import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Phone, Shield, Clock, MapPin, Award } from 'lucide-react';
import { PHONES, DISTRICTS, CORPORATE } from '../content';

const TRUST_POINTS = [
  { icon: Shield, text: '24 ay garanti', color: 'var(--green)' },
  { icon: Clock, text: '7/24 kesintisiz', color: 'var(--blue)' },
  { icon: Award, text: 'Sertifikalı ekip', color: '#ff9500' },
  { icon: MapPin, text: '12+ ilçe', color: 'var(--red)' },
];

const CHECKS = [
  'Tamir araç başında yapılır',
  'Servise götürmeye gerek yok',
  'Tüm marka ve modeller',
  'Kamyon · Otobüs · Motosiklet',
  '7/24 — tatil-hafta sonu dahil',
  'Ortalama 30 dk müdahale',
];

export default function About() {
  return (
    <section data-testid="about-section" className="section" style={{ background: 'var(--bg)', position: 'relative', overflow: 'hidden' }}>
      {/* Glow */}
      <div style={{
        position: 'absolute',
        top: '30%',
        right: '10%',
        width: 500,
        height: 500,
        background: 'radial-gradient(ellipse, rgba(229,51,51,.05) 0%, transparent 65%)',
        pointerEvents: 'none',
      }} />

      <div className="wrap">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '5rem',
            alignItems: 'center',
          }}
          className="about-grid"
        >
          {/* ── Image column ── */}
          <motion.div
            initial={{ opacity: 0, x: -32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: .7, ease: [.25, .46, .45, .94] }}
            style={{ position: 'relative' }}
          >
            <div
              style={{
                borderRadius: 'var(--rxl)',
                overflow: 'hidden',
                border: '1px solid var(--border)',
                lineHeight: 0,
                position: 'relative',
              }}
            >
              <img
                src="/images/about/tamir-degisim.webp"
                alt="Lastik tamir & değişim"
                className="about-img"
                style={{
                  width: '100%',
                  height: 500,
                  objectFit: 'cover',
                  display: 'block',
                }}
                loading="lazy"
              />
              {/* Image overlay gradient */}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to top, rgba(5,5,13,.4) 0%, transparent 50%)',
              }} />
            </div>

            {/* Floating 7/24 badge */}
            <div
              className="about-badge-float"
              style={{
                position: 'absolute',
                bottom: '-1.5rem',
                right: '-1.5rem',
                background: 'linear-gradient(135deg, var(--red) 0%, var(--red2) 100%)',
                borderRadius: 'var(--rl)',
                padding: '1.5rem 1.75rem',
                textAlign: 'center',
                boxShadow: '0 12px 48px rgba(229,51,51,.35)',
                minWidth: 140,
                border: '1px solid rgba(255,255,255,.1)',
              }}
            >
              <div
                className="about-badge-num"
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 800,
                  fontSize: '2.25rem',
                  color: '#fff',
                  lineHeight: 1,
                }}
              >
                7/24
              </div>
              <div
                style={{
                  fontSize: '.8rem',
                  color: 'rgba(255,255,255,.8)',
                  marginTop: '.375rem',
                  fontWeight: 600,
                  letterSpacing: '.02em',
                }}
              >
                Kesintisiz
              </div>
            </div>
          </motion.div>

          {/* ── Text column ── */}
          <motion.div
            initial={{ opacity: 0, x: 32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: .7, delay: .15, ease: [.25, .46, .45, .94] }}
          >
            <div className="pill pill-red" style={{ marginBottom: '1.25rem' }}>
              Güvenilir Hizmet
            </div>

            <h2 className="h2" style={{ marginBottom: '1.25rem' }}>
              Kocaeli'nin En Hızlı
              <br />
              <span style={{ color: 'var(--blue)' }}>Mobil Lastik Servisi</span>
            </h2>

            <p className="body-lg" style={{ marginBottom: '1rem' }}>
              {CORPORATE.about}
            </p>

            <p className="body" style={{ marginBottom: '2rem', color: 'var(--text2)' }}>
              Araç tipi ve marka fark etmeksizin, gün ve gecenin her saatinde
              profesyonel ekibimizle yanınızdayız.
            </p>

            {/* Trust points */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '.875rem',
                marginBottom: '2rem',
              }}
            >
              {TRUST_POINTS.map(({ icon: Icon, text, color }) => (
                <div
                  key={text}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '.625rem',
                    background: 'var(--card)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '.875rem 1rem',
                    transition: 'all .2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border2)';
                    e.currentTarget.style.background = 'var(--card2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.background = 'var(--card)';
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: `${color}15`,
                      border: `1px solid ${color}30`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color,
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={15} strokeWidth={2} />
                  </div>
                  <span
                    style={{
                      fontSize: '.875rem',
                      fontWeight: 600,
                      color: 'var(--text)',
                    }}
                  >
                    {text}
                  </span>
                </div>
              ))}
            </div>

            {/* Checklist */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '.625rem',
                marginBottom: '2.25rem',
              }}
            >
              {CHECKS.map((c) => (
                <div
                  key={c}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '.5rem',
                  }}
                >
                  <CheckCircle2
                    size={16}
                    style={{
                      color: 'var(--green)',
                      marginTop: '1px',
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: '.875rem',
                      color: 'var(--text2)',
                      lineHeight: 1.5,
                    }}
                  >
                    {c}
                  </span>
                </div>
              ))}
            </div>

            {/* Districts */}
            <p className="eyebrow" style={{ marginBottom: '.75rem', color: 'var(--text2)' }}>
              Hizmet Bölgeleri
            </p>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '.5rem',
                marginBottom: '2.25rem',
              }}
            >
              {DISTRICTS.slice(0, 8).map((d) => (
                <span key={d} className="chip">
                  {d}
                </span>
              ))}
            </div>

            <a
              href={PHONES.main_tel}
              className="btn btn-primary btn-lg num ring"
              style={{ gap: '.625rem' }}
            >
              <Phone size={17} /> Hemen Ara — {PHONES.main}
            </a>
          </motion.div>
        </div>
      </div>
      <style>{`
        @media(max-width: 768px) {
          .about-grid {
            grid-template-columns: 1fr !important;
            gap: 3rem !important;
          }
          .about-badge-float {
            right: .75rem !important;
            bottom: -.75rem !important;
            padding: 1rem 1.25rem !important;
            min-width: auto !important;
          }
          .about-badge-float .about-badge-num {
            font-size: 1.5rem !important;
          }
          .about-img {
            height: 320px !important;
          }
        }
      `}</style>
    </section>
  );
}
